import React, {useState, useEffect} from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { Box, Card, Divider, Grid2 as Grid, Link, Stack, Typography, useTheme } from '@mui/material';
import Tab from '@mui/material/Tab';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';

//import schedules
import ReportAmendReasonBlock from '../components/ReportAmendReasonBlock';
import ReportAmendedReasonBlock from '../components/ReportAmendedReasonBlock';
import IndependentExpenditureItem from '../../../types/reports/independentExpenditureItem';
import IndependentExpenditureReportContent from '../../../types/reports/independentExpenditureReportContent';
import { ReceiptItem } from '../../../types/reports/receiptItem';
import ReceiptsScheduleTable from '../components/ReceiptsScheduleTable';
import IndependentExpendituresScheduleTable from '../components/IndependentExpendituresScheduleTable';

interface ReportProps {
   reportId: number;
   currentReport?: any
}

const IndependentExpendituresReport = (reportProps: ReportProps): JSX.Element => {

    const theme = useTheme();
    const { showBoundary } = useErrorBoundary();  
    const [scheduleValue, setScheduleValue] = useState('summary');
    const fieldWidth = { xs: "50%", sm: "40%", md: "40%" };

    const [receiptsRows, setReceiptsRows] = useState<ReceiptItem[]>();
    const [expendituresRows, setExpendituresRows] = useState<IndependentExpenditureItem[]>();
    const [report, setReport] = useState<IndependentExpenditureReportContent>();

    useEffect(() => {
            if(reportProps.currentReport){
                let report = reportProps.currentReport;
                setScheduleValue('summary');
                setReport(report);
                setReceiptsRows(report.receipts);
                setExpendituresRows(report.expenditures);
            }
            else{
                getFullReport();
            }
    }, [reportProps.reportId])

    const getFullReport = async () => {

        try{
            await fetch(`${process.env.REACT_APP_API_BASE_URL}/miscreport/${reportProps.reportId}`)
            .then(result => {
            if (!result.ok) {
                showBoundary(new Error('Failed to get independent expenditure report content.'));
            }
            return result.json();
        })
        .then(result => {

            let report = result;
            setScheduleValue('summary');
            setReport(report);
            setReceiptsRows(report.receipts);
            setExpendituresRows(report.expenditures);
            return;
        });
        }catch(error){
            console.error('Uncaught Error:', error);
        }
    }

  const handleScheduleChange = (event: React.SyntheticEvent, newValue: string) => {
    setScheduleValue(newValue);
  };

  return (
    <Box sx={{ width: '100%', typography: 'body1' }}>
      <TabContext value={scheduleValue}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <TabList onChange={handleScheduleChange} aria-label="periodic report schedules">
            <Tab className="report-tab-label" label="Summary" value="summary" />
            {
                (report?.receipts?.length ?? 0) > 0 &&
                <Tab className="report-tab-label" label={`Receipts [${report?.receipts?.length}]`} value="receipts" />
            }
            {
                (report?.expenditures?.length ?? 0) > 0 &&
                <Tab className="report-tab-label" label={`Expenditures [${report?.expenditures?.length}]`} value="expenditures" />
            }
          </TabList>
        </Box>
        <TabPanel value="summary">
            <Grid container spacing={2}>
                {/*Filer Info*/}
                <Grid size={12}>
                    <Box
                        component={Card}
                        boxShadow={0}
                        sx={{ bgcolor: 'alternate.light' }}
                        border={`1px solid ${theme.palette.divider}`}
                    >
                        <Box padding={{ xs: 2, sm: 3 }}>
                            <Stack direction="row" spacing={1} marginBottom={2}>
                                <Typography component={"i"} variant={'body1'} width={fieldWidth}>
                                     Filer:
                                </Typography>
                                <Typography variant={'body1'} fontWeight={550} align='left' width={fieldWidth}>
                                    <div dangerouslySetInnerHTML={{__html: `${report?.filingEntityFull}`}} />
                                </Typography>
                            </Stack>
                            <Stack direction="row" spacing={1} marginBottom={2}>
                                <Typography component={"i"} variant={'body1'} width={fieldWidth}>
                                     Filed On:
                                </Typography>
                                <Typography variant={'body1'} align='left' width={fieldWidth}>
                                     {report?.filingDate}
                                </Typography>
                            </Stack>
                            <Stack direction="row" spacing={1} marginBottom={2}>
                                <Typography component={"i"} variant={'body1'} width={fieldWidth}>
                                    Reporting Period:
                                </Typography>
                                <Typography variant={'body1'} align='left' width={fieldWidth} color="primary">
                                     {report?.reportingPeriod}
                                </Typography>
                            </Stack>
                        </Box>
                    </Box>
                </Grid>
                {/*Amendment Reason*/}
                {
                    report?.isAmendment && 
                    <ReportAmendReasonBlock reason={report?.amendmentReason} previousReportId={report?.previousReportId}></ReportAmendReasonBlock>
                }
                {/*Being Amended*/}
                {
                    report?.isAmended && 
                    <ReportAmendedReasonBlock nextReportId={report?.nextReportId}></ReportAmendedReasonBlock>
                }
                {/*Financial Summary*/}
                <Grid size={12}>
                    <Box
                        component={Card}
                        boxShadow={0}
                        sx={{ bgcolor: 'alternate.light' }}
                        border={`1px solid ${theme.palette.divider}`}
                    >
                        <Box padding={{ xs: 2, sm: 3 }}>
                            <Stack direction="row" spacing={1} marginBottom={2} display={'flex'} justifyContent={'space-between'}>
                                <Typography component={"i"} variant={'body1'}>
                                    Expenditure Total:
                                </Typography>
                                <Typography variant={'body1'} align='right'>
                                     {report?.expenditureTotal}
                                </Typography>
                            </Stack>
                            {
                                report?.reportType === 'EC' &&
                                 <Stack direction="row" spacing={1} marginBottom={2} display={'flex'} justifyContent={'space-between'}>
                                    <Typography component={"i"} variant={'body1'}>
                                        Itemized Contributions (Over $250):
                                    </Typography>
                                    <Typography variant={'body1'} align='right'>
                                     {report?.receiptTotal}
                                    </Typography>
                                </Stack>
                            }
                            {
                                report?.reportType !== 'PSR' &&
                                <Stack direction="row" spacing={1} marginBottom={2} marginTop={2} display={'flex'} justifyContent={'space-between'}>
                                    <Typography component={"i"} variant={'body1'}>
                                        Candidates/Position:
                                    </Typography>
                                    <Typography variant={'body1'} align='right'>
                                        <div dangerouslySetInnerHTML={{__html: `${report?.candidateSpendingBreakdown}`}} />
                                        {report?.candidateListing}
                                    </Typography>
                                </Stack>
                            }
                            
                        </Box>
                    </Box>
                </Grid>
            </Grid>
        </TabPanel>
        <TabPanel value="receipts">
            <ReceiptsScheduleTable tableRows={receiptsRows}></ReceiptsScheduleTable>
            <Box padding={{ xs: 2, sm: 3 }}>
                <Stack direction="row" spacing={1} marginBottom={2} display={'flex'} justifyContent={'space-between'}>
                    <Typography variant={'body1'}>
                        Itemized Contributions Total:
                    </Typography>
                    <Typography variant={'body1'} fontWeight={550} align='right'>
                        {report?.receiptTotal}
                    </Typography>
                </Stack>
            </Box>
        </TabPanel>
        <TabPanel value="expenditures">
            <IndependentExpendituresScheduleTable ieTableRows={expendituresRows}></IndependentExpendituresScheduleTable>
            <Box padding={{ xs: 2, sm: 3 }}>
                <Stack direction="row" spacing={1} marginBottom={2} display={'flex'} justifyContent={'space-between'}>
                    <Typography variant={'body1'}>
                        Expenditure Total:
                    </Typography>
                    <Typography variant={'body1'} fontWeight={550} align='right'>
                        {report?.expenditureTotal}
                    </Typography>
                </Stack>
            </Box>
        </TabPanel>
      </TabContext>
    </Box>
  );
}

export default IndependentExpendituresReport;